import {useEffect, useState} from 'react';
import {Platform, useWindowDimensions} from 'react-native';

/** Web viewport width below which layouts reflow (320 px = 1280 px at 400% zoom,
 *  640 px = 1280 px at 200% zoom). */
export const NARROW_VIEWPORT_WIDTH = 700;

/** Web viewport height below which (together with a narrow width) the page is
 *  considered extremely zoomed in (e.g. 256 px tall = 1024 px at 400% zoom, 512 px at 200%).
 *  Real phones are far taller in portrait and wider in landscape, so they never
 *  match both limits. */
export const ZOOMED_VIEWPORT_HEIGHT = 560;

export const isZoomedViewport = (width: number, height: number): boolean =>
  width > 0 &&
  height > 0 &&
  width < NARROW_VIEWPORT_WIDTH &&
  height < ZOOMED_VIEWPORT_HEIGHT;

/** Web-only: true while the page is extremely zoomed in (see isZoomedViewport). */
export const useIsZoomed = (): boolean => {
  const {width, height} = useWindowDimensions();
  return Platform.OS === 'web' && isZoomedViewport(width, height);
};

/**
 * The navigator keeps earlier screens mounted underneath the active one (e.g.
 * Shop Home stays in the page after the technician PIN is entered), so several
 * elements can carry the `site-header` id. `getElementById` would return the
 * first — the *old* screen's header, which may be a different height — so pick
 * the last visible one instead: the header of the screen on top.
 */
const findActiveSiteHeader = (): HTMLElement | null => {
  const all = Array.from(
    document.querySelectorAll<HTMLElement>('[id="site-header"]'),
  );
  const visible = all.filter(el => el.getClientRects().length > 0);
  const pool = visible.length > 0 ? visible : all;
  return pool[pool.length - 1] ?? null;
};

/**
 * Web-only: measures the app header (`#site-header`, see Header.tsx) and keeps
 * the value up to date as it wraps / resizes. When zoomed in (narrow viewport)
 * the header wraps onto several lines, so screens that were sized to a full
 * `100vh` push their bottom content out of the viewport. Those screens use this
 * height to size themselves to `calc(100vh - headerHeight)` instead.
 *
 * Returns 0 on native or when the header is not mounted.
 */
export const useSiteHeaderHeight = (): number => {
  const zoomed = useIsZoomed();
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    // The value is only consumed at extreme zoom, so do no work (and attach no
    // observers) at normal zoom.
    if (!zoomed || typeof document === 'undefined') {
      return undefined;
    }
    let current: HTMLElement | null = null;
    let resizeObserver: ResizeObserver | undefined;

    const measure = () => {
      const el = findActiveSiteHeader();
      const next = el ? Math.round(el.getBoundingClientRect().height) : 0;
      setHeaderHeight(prev => (prev === next ? prev : next));
      // The header element can be (re)created after this hook mounts — e.g. after
      // logout / login — so always re-attach the resize observer to the live node.
      if (el !== current) {
        resizeObserver?.disconnect();
        current = el;
        if (el && typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(measure);
          resizeObserver.observe(el);
        }
      }
    };

    measure();
    // Watch for the header being added / replaced in the DOM.
    const mutationObserver =
      typeof MutationObserver === 'undefined'
        ? undefined
        : new MutationObserver(measure);
    mutationObserver?.observe(document.body, {childList: true, subtree: true});

    return () => {
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [zoomed]);

  return headerHeight;
};
